import React, { useState } from 'react';
import { Shield, Sparkles, Video, Award, Star, Calendar, Clock, User, Heart, Send, CheckCircle } from 'lucide-react';
import { submitContactMessage } from '../api/client';

const VipPage = () => {
    const [lang, setLang] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.language === 'ar') return 'ar';
            }
        } catch (_) {}
        return 'ar'; // Default to Arabic for brand loyalty
    });

    const isRtl = lang === 'ar';
    const [bookingSubmitted, setBookingSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', date: '', time: '', notes: '' });

    const toggleLanguage = () => {
        setLang(prev => (prev === 'ar' ? 'en' : 'ar'));
    };

    const handleBooking = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email) return;
        setSubmitting(true);
        try {
            await submitContactMessage({
                name: formData.name,
                email: formData.email,
                subject: `[VIP Concierge Booking] Date: ${formData.date} - Time: ${formData.time}`,
                message: `Client: ${formData.name}\nEmail: ${formData.email}\nPreferred Date: ${formData.date}\nTime: ${formData.time}\nNotes: ${formData.notes || 'None'}`
            }).catch(() => {});
            setBookingSubmitted(true);
        } catch (_) {
            setBookingSubmitted(true);
        } finally {
            setSubmitting(false);
        }
    };

    const t = {
        title: { ar: "نادي كبار الشخصيات لوكسفونو", en: "LuxVono VIP Club" },
        subtitle: { ar: "عالم من المزايا الحصرية والخدمات المصممة خصيصاً لنخبة مقتني الفخامة.", en: "An exclusive realm of curated benefits and bespoke services tailored for elite collectors." },
        privateConsultation: { ar: "طلب استشارة شخصية 1-على-1", en: "Request Private 1-on-1 Consultation" },
        bookConsultation: { ar: "احجز جلسة فيديو مباشرة مع مستشارك الشخصي لتنسيق مظهرك واختيار أرقى الماركات.", en: "Schedule a live high-fidelity video session with your personal concierge shopper." },
        nameLabel: { ar: "الاسم الكامل", en: "Full Name" },
        emailLabel: { ar: "البريد الإلكتروني", en: "Email Address" },
        dateLabel: { ar: "التاريخ المفضل", en: "Preferred Date" },
        timeLabel: { ar: "الوقت المفضل", en: "Preferred Time" },
        notesLabel: { ar: "ملاحظات خاصة أو طلبات معينة", en: "Special Requests or Designer Brands of Interest" },
        submitBtn: { ar: "طلب حجز استشارة VIP", en: "Book Private VIP Consultation" },
        successMsg: { ar: "تم استلام طلب الحجز بنجاح! سيتصل بك مستشارك الشخصي لتأكيد الموعد قريباً.", en: "Booking request received! Your dedicated concierge will contact you shortly to confirm." },
        benefit1Title: { ar: "وصول مبكر للقطع النادرة", en: "Priority Access to Rare Pieces" },
        benefit1Desc: { ar: "كن أول من يستكشف ويقتني التشكيلات الحصرية والقطع الفريدة قبل طرحها في المتجر الرئيسي.", en: "Be the first to secure unique collector items and rare editions before they launch." },
        benefit2Title: { ar: "مساعد تسوق مخصص", en: "Dedicated Personal Shopper" },
        benefit2Desc: { ar: "مستشار موضة متاح على مدار الساعة عبر الفيديو لتلبية احتياجاتك واقتراح الأنسب لك.", en: "A high-end personal concierge available 24/7 via dynamic video feeds to serve your wardrobe." },
        benefit3Title: { ar: "تغليف هدايا ملكي وشحن مجاني", en: "Royal Wrapping & Priority Delivery" },
        benefit3Desc: { ar: "تغليف يدوي فاخر بأوراق الحرير مع شحن جوي سريع مؤمن بالكامل لجميع أنحاء العالم.", en: "Hand-wrapped in fine silk papers with fully secured, prioritized worldwide air cargo." }
    };

    return (
        <div 
            className="vip-page-root"
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
                background: '#08080C',
                color: '#FFFFFF',
                minHeight: '100vh',
                fontFamily: "'Outfit', 'Inter', 'Noto Sans Arabic', sans-serif",
                overflowX: 'hidden',
                paddingBottom: '80px',
                position: 'relative'
            }}
        >
            <style dangerouslySetInnerHTML={{__html: `
                .vip-page-root {
                    background: radial-gradient(circle at 50% 0%, #16141F 0%, #08080C 60%) !important;
                }
                .vip-glow-1 {
                    position: absolute;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(197,168,128,0.15) 0%, rgba(197,168,128,0) 70%);
                    top: -50px;
                    right: -100px;
                    filter: blur(50px);
                    pointer-events: none;
                }
                .vip-glow-2 {
                    position: absolute;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(123,97,255,0.06) 0%, rgba(123,97,255,0) 70%);
                    bottom: 10%;
                    left: -150px;
                    filter: blur(80px);
                    pointer-events: none;
                }
                .vip-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 8%;
                    border-bottom: 1px solid rgba(197, 168, 128, 0.1);
                    background: rgba(8, 8, 12, 0.4);
                    backdrop-filter: blur(20px);
                }
                .vip-logo {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: 5px;
                    background: linear-gradient(135deg, #FFFFFF 30%, #C5A880 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 0 30px rgba(197, 168, 128, 0.2);
                }
                .vip-lang-btn {
                    background: rgba(197, 168, 128, 0.08);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    border-radius: 40px;
                    padding: 8px 18px;
                    color: #C5A880;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                }
                .vip-lang-btn:hover {
                    background: rgba(197, 168, 128, 0.18);
                    transform: translateY(-2px);
                }
                .vip-hero {
                    text-align: center;
                    max-width: 800px;
                    margin: 60px auto 40px auto;
                    padding: 0 20px;
                }
                .vip-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: linear-gradient(90deg, rgba(197,168,128,0.15) 0%, rgba(197,168,128,0.02) 100%);
                    border: 1px solid rgba(197, 168, 128, 0.3);
                    padding: 6px 20px;
                    border-radius: 50px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #C5A880;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                    margin-bottom: 20px;
                }
                .vip-title {
                    font-size: clamp(32px, 5vw, 48px);
                    font-weight: 800;
                    line-height: 1.2;
                    margin-bottom: 20px;
                }
                .vip-subtitle {
                    font-size: 16px;
                    color: #A3A3A3;
                    line-height: 1.6;
                }
                .vip-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 60px;
                    max-width: 1200px;
                    margin: 50px auto;
                    padding: 0 6%;
                    align-items: start;
                }
                @media (max-width: 968px) {
                    .vip-grid {
                        grid-template-columns: 1fr;
                        gap: 50px;
                    }
                }
                .vip-benefits-col {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }
                .vip-card {
                    background: rgba(255, 255, 255, 0.01);
                    border: 1px solid rgba(255, 255, 255, 0.04);
                    border-radius: 20px;
                    padding: 28px;
                    display: flex;
                    gap: 20px;
                    align-items: flex-start;
                    transition: all 0.3s ease;
                }
                .vip-card:hover {
                    border-color: rgba(197, 168, 128, 0.2);
                    background: rgba(197, 168, 128, 0.02);
                    transform: translateY(-3px);
                }
                .vip-card-icon {
                    background: rgba(197, 168, 128, 0.08);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    border-radius: 14px;
                    padding: 12px;
                    color: #C5A880;
                    flex-shrink: 0;
                }
                .vip-card-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #FFFFFF;
                    margin-bottom: 8px;
                }
                .vip-card-desc {
                    font-size: 14px;
                    color: #8C8C8C;
                    line-height: 1.6;
                }
                .vip-form-panel {
                    background: rgba(13, 13, 20, 0.7);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    border-radius: 24px;
                    padding: 40px;
                    backdrop-filter: blur(30px);
                    box-shadow: 0 30px 60px rgba(0,0,0,0.5);
                    position: relative;
                }
                .vip-form-header {
                    margin-bottom: 30px;
                }
                .vip-form-title {
                    font-size: 22px;
                    font-weight: 800;
                    margin-bottom: 8px;
                    color: #FFFFFF;
                }
                .vip-form-desc {
                    font-size: 13px;
                    color: #8C8C8C;
                    line-height: 1.5;
                }
                .vip-input-group {
                    margin-bottom: 20px;
                }
                .vip-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 600;
                    color: #C5A880;
                    margin-bottom: 8px;
                }
                .vip-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 14px 16px;
                    color: #FFFFFF;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }
                .vip-input:focus {
                    outline: none;
                    border-color: #C5A880;
                    background: rgba(197, 168, 128, 0.03);
                    box-shadow: 0 0 15px rgba(197, 168, 128, 0.1);
                }
                .vip-datetime-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }
                .vip-submit-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #C5A880 0%, #A28256 100%);
                    border: none;
                    border-radius: 14px;
                    padding: 16px 24px;
                    color: #000000;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    margin-top: 30px;
                    box-shadow: 0 10px 25px rgba(197, 168, 128, 0.2);
                    transition: all 0.3s ease;
                }
                .vip-submit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 30px rgba(197, 168, 128, 0.35);
                }
                .vip-success-card {
                    text-align: center;
                    padding: 40px 10px;
                }
                .vip-success-icon {
                    color: #4ADE80;
                    margin-bottom: 20px;
                }
            `}} />

            <div className="vip-glow-1"></div>
            <div className="vip-glow-2"></div>

            <header className="vip-header">
                <div className="vip-logo">LUXVONO</div>
                <button className="vip-lang-btn" onClick={toggleLanguage}>
                    <Sparkles size={14} />
                    <span>{isRtl ? 'English' : 'العربية'}</span>
                </button>
            </header>

            <main>
                <div className="vip-hero">
                    <span className="vip-badge">
                        <Award size={14} style={{ color: '#C5A880' }} />
                        {isRtl ? "نخبة لوكسفونو الفاخرة" : "LUXVONO ELITE CONCIERGE"}
                    </span>
                    <h1 className="vip-title">
                        {isRtl ? t.title.ar : t.title.en}
                    </h1>
                    <p className="vip-subtitle">
                        {isRtl ? t.subtitle.ar : t.subtitle.en}
                    </p>
                </div>

                <div className="vip-grid">
                    <div className="vip-benefits-col">
                        <div className="vip-card">
                            <div className="vip-card-icon"><Star size={24} /></div>
                            <div>
                                <h3 className="vip-card-title">{isRtl ? t.benefit1Title.ar : t.benefit1Title.en}</h3>
                                <p className="vip-card-desc">{isRtl ? t.benefit1Desc.ar : t.benefit1Desc.en}</p>
                            </div>
                        </div>

                        <div className="vip-card">
                            <div className="vip-card-icon"><Video size={24} /></div>
                            <div>
                                <h3 className="vip-card-title">{isRtl ? t.benefit2Title.ar : t.benefit2Title.en}</h3>
                                <p className="vip-card-desc">{isRtl ? t.benefit2Desc.ar : t.benefit2Desc.en}</p>
                            </div>
                        </div>

                        <div className="vip-card">
                            <div className="vip-card-icon"><Shield size={24} /></div>
                            <div>
                                <h3 className="vip-card-title">{isRtl ? t.benefit3Title.ar : t.benefit3Title.en}</h3>
                                <p className="vip-card-desc">{isRtl ? t.benefit3Desc.ar : t.benefit3Desc.en}</p>
                            </div>
                        </div>
                    </div>

                    <div className="vip-form-col">
                        <div className="vip-form-panel">
                            {!bookingSubmitted ? (
                                <form onSubmit={handleBooking}>
                                    <div className="vip-form-header">
                                        <h2 className="vip-form-title">{isRtl ? t.privateConsultation.ar : t.privateConsultation.en}</h2>
                                        <p className="vip-form-desc">{isRtl ? t.bookConsultation.ar : t.bookConsultation.en}</p>
                                    </div>

                                    <div className="vip-input-group">
                                        <label className="vip-label">{isRtl ? t.nameLabel.ar : t.nameLabel.en}</label>
                                        <input 
                                            type="text" 
                                            className="vip-input" 
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>

                                    <div className="vip-input-group">
                                        <label className="vip-label">{isRtl ? t.emailLabel.ar : t.emailLabel.en}</label>
                                        <input 
                                            type="email" 
                                            className="vip-input" 
                                            required
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        />
                                    </div>

                                    <div className="vip-datetime-row">
                                        <div className="vip-input-group">
                                            <label className="vip-label">{isRtl ? t.dateLabel.ar : t.dateLabel.en}</label>
                                            <input 
                                                type="date" 
                                                className="vip-input" 
                                                required
                                                value={formData.date}
                                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                            />
                                        </div>
                                        <div className="vip-input-group">
                                            <label className="vip-label">{isRtl ? t.timeLabel.ar : t.timeLabel.en}</label>
                                            <input 
                                                type="time" 
                                                className="vip-input" 
                                                required
                                                value={formData.time}
                                                onChange={e => setFormData({ ...formData, time: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="vip-input-group">
                                        <label className="vip-label">{isRtl ? t.notesLabel.ar : t.notesLabel.en}</label>
                                        <textarea 
                                            className="vip-input" 
                                            rows="3" 
                                            style={{ resize: 'none' }}
                                            value={formData.notes}
                                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                        />
                                    </div>

                                    <button type="submit" className="vip-submit-btn">
                                        <Calendar size={18} />
                                        <span>{isRtl ? t.submitBtn.ar : t.submitBtn.en}</span>
                                    </button>
                                </form>
                            ) : (
                                <div className="vip-success-card">
                                    <CheckCircle size={60} className="vip-success-icon" />
                                    <h2 className="vip-form-title" style={{ fontSize: '24px', marginBottom: '16px' }}>
                                        {isRtl ? "تمت جدولة الطلب" : "Consultation Requested"}
                                    </h2>
                                    <p className="vip-card-desc" style={{ fontSize: '15px' }}>
                                        {isRtl ? t.successMsg.ar : t.successMsg.en}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default VipPage;
